/**
 * The Studio Handbook site: its own deployment, separate from Studio Admin.
 * Anyone at RapidSOS can sign in, but the content comes from the API, which
 * hands it only to holders of Handbook Access (or platform admins),
 * so the site's bundle carries no handbook text at all.
 */
import { BookOpen, Download, LogOut } from 'lucide-react';
import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { AUTH_CONFIGURED, AuthProvider, useAuthSession } from '@/auth';
import { describeAuthError } from '@/auth/authErrorMessage';
import { sanitizeReturnTo } from '@/auth/returnTo';
import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';

import { HandbookContext } from './HandbookContext';
import { HandbookPage } from './HandbookPage';
import { HandbookPrint } from './HandbookPrint';
import { HandbookSearch } from './HandbookSearch';
import { type HandbookLoad, useHandbookDocument } from './useHandbookDocument';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-2xl border border-border bg-card px-8 py-10 text-center shadow-sm">
        <div className="flex items-center gap-2 text-foreground">
          <RapidSOSMark className="h-8 w-8" animation={DEFAULT_MARK_ANIMATION} />
          <RapidSOSWordmark className="h-8" />
        </div>
        {children}
      </div>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function SignIn() {
  const { loginWithRedirect } = useAuthSession();
  return (
    <Centered>
      <div>
        <h1 className="text-xl font-semibold text-foreground">Studio Handbook</h1>
        <p className="mt-1 text-sm text-muted-foreground">How Studio works, and how to extend it. Sign in with your RapidSOS account.</p>
      </div>
      <Button
        type="button"
        className="w-full"
        onClick={() => void loginWithRedirect({ appState: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` } })}
      >
        Sign in
      </Button>
    </Centered>
  );
}

function signOut(logout: ReturnType<typeof useAuthSession>['logout']) {
  void logout({ logoutParams: { returnTo: window.location.origin } });
}

function NoAccess() {
  const { logout, user } = useAuthSession();
  return (
    <Centered>
      <div>
        <h1 className="text-xl font-semibold text-foreground">You don’t have the Handbook yet</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          The Studio Handbook is open to people with the <strong className="text-foreground">Handbook Access</strong> role. Ask a Studio
          platform admin to give it to {user?.email ? <span className="text-foreground">{user.email}</span> : 'you'} in Studio Admin → Users.
        </p>
      </div>
      {AUTH_CONFIGURED ? (
        <Button type="button" variant="outline" className="w-full" onClick={() => signOut(logout)}>
          Use a different account
        </Button>
      ) : null}
    </Centered>
  );
}

function Header() {
  const { logout, user } = useAuthSession();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur print:hidden">
      <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 text-sm font-semibold text-foreground">
          <RapidSOSMark className="h-6 w-6" />
          <span className="hidden sm:inline">Studio Handbook</span>
          <BookOpen className="h-4 w-4 text-brand sm:hidden" />
        </Link>
        <div className="ml-auto flex min-w-0 items-center gap-2">
          <div className="hidden w-72 md:block">
            <HandbookSearch shortcut />
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/print?download=1">
              <Download className="mr-1.5 h-3.5 w-3.5" />
              PDF
            </Link>
          </Button>
          {AUTH_CONFIGURED ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => signOut(logout)} title={user?.email ? `Sign out ${user.email}` : 'Sign out'}>
              <LogOut className="h-3.5 w-3.5" />
              <span className="sr-only">Sign out</span>
            </Button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function Loaded({ load }: { load: HandbookLoad }) {
  if (load.state === 'loading') return <Spinner label="Opening the handbook" />;
  if (load.state === 'no-access') return <NoAccess />;
  if (load.state === 'failed') {
    return (
      <Centered>
        <div>
          <h1 className="text-xl font-semibold text-foreground">The handbook didn’t load</h1>
          <p className="mt-2 text-sm text-muted-foreground">Trying again in a few seconds.</p>
        </div>
      </Centered>
    );
  }
  return (
    <HandbookContext.Provider value={load.handbook}>
      <Routes>
        <Route path="/print" element={<HandbookPrint />} />
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-background">
              <Header />
              <Routes>
                <Route path="/" element={<HandbookPage />} />
                <Route path="/handbook/*" element={<LegacyHandbookPath />} />
                <Route path="/:group/:slug" element={<HandbookPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          }
        />
      </Routes>
    </HandbookContext.Provider>
  );
}

/** `/handbook/<group>/<slug>`, the form Studio Admin used, lands on the same page here. */
function LegacyHandbookPath() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/handbook/, '') || '/';
  return <Navigate to={`${rest}${location.hash}`} replace />;
}

function SignedInHandbook() {
  const { isAuthenticated, isLoading, error } = useAuthSession();
  const load = useHandbookDocument(isAuthenticated);
  if (error) {
    const notice = describeAuthError(error);
    return (
      <Centered>
        <div>
          <h1 className="text-xl font-semibold text-foreground">{notice.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{notice.body}</p>
        </div>
        <Button type="button" className="w-full" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Centered>
    );
  }
  if (isLoading) return <Spinner label="Signing in" />;
  if (!isAuthenticated) return <SignIn />;
  return <Loaded load={load} />;
}

function LocalHandbook() {
  return <Loaded load={useHandbookDocument(true)} />;
}

function AuthProviderWithNavigate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return <AuthProvider onRedirectCallback={(returnTo) => navigate(sanitizeReturnTo(returnTo), { replace: true })}>{children}</AuthProvider>;
}

export function HandbookApp() {
  return (
    <BrowserRouter>
      {AUTH_CONFIGURED ? (
        <AuthProviderWithNavigate>
          <SignedInHandbook />
        </AuthProviderWithNavigate>
      ) : (
        <LocalHandbook />
      )}
    </BrowserRouter>
  );
}
